import { useState, useEffect } from "react";
import { Copy, Check, Clock, Send, MessageCircle, Link2, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { sendAndConfirm } from "./sendUtils";

/**
 * FormBlock — présentation HARMONISÉE des formulaires voyageur (petit-déj, bain
 * fleuri, couchage). Les trois blocs se ressemblent : icône + nom, statut, bouton
 * principal « Envoyer sur WhatsApp », puis SMS / Copier le lien / Prévisualiser.
 *
 * Le lien (aperçu WhatsApp dédié) passe par le wrapper backend /api/rg/{wrapperPath}/{token}
 * qui sert les balises Open Graph (photo + titre propres à chaque formulaire) puis
 * redirige vers le formulaire sécurisé /{previewPath}/{token} rattaché à la réservation.
 */

export function FormStatusBadge({ state, testid }) {
  const map = {
    to_send: { label: "À envoyer", bg: "var(--rg-border-soft)", color: "var(--rg-text-2)", Icon: Send },
    sent: { label: "Formulaire envoyé — en attente de réponse ❓", bg: "#fff3cd", color: "#8a6d1a", Icon: Clock },
    received: { label: "Réponse reçue ✅", bg: "#e6f4ea", color: "#2f7a43", Icon: Check },
  };
  const s = map[state] || map.to_send;
  return (
    <span data-testid={testid || `rg-form-status-${state}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999, background: s.bg, color: s.color }}>
      <s.Icon size={12} /> {s.label}
    </span>
  );
}

export default function FormBlock({
  icon, title, subtitle, state, phone, busy,
  fetchToken, wrapperPath, previewPath, buildMessage,
  onSent, onCancel, onReopen, receivedNode, testid,
  anchorId,
}) {
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);

  const cleanPhone = (phone || "").replace(/[^\d+]/g, "");
  const hasPhone = !!cleanPhone;

  // Pré-charge le token dès l'ouverture pour garder window.open dans le geste utilisateur.
  useEffect(() => {
    let alive = true;
    if (!token) fetchToken().then((t) => { if (alive) setToken(t); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const wrapperLink = (t) => `${window.location.origin}/api/rg/${wrapperPath}/${t}`;
  const previewLink = token ? `${window.location.origin}/${previewPath}/${token}` : null;

  const copyLink = async () => {
    let t = token;
    if (!t) { try { t = await fetchToken(); setToken(t); } catch { /* ignore */ } }
    const link = wrapperLink(t);
    try { await navigator.clipboard.writeText(link); } catch { window.prompt("Copiez le lien :", link); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const sendVia = (channel) => {
    const link = wrapperLink(token);
    const msg = buildMessage(link);
    let confirmed = false;
    try {
      confirmed = sendAndConfirm({ channel, message: msg, phone });
    } catch (e) {
      if (e.code === "no-phone") {
        toast.error("Numéro WhatsApp manquant ou invalide", { description: "Ajoute un numéro valide sur la fiche avant d'envoyer." });
        return;
      }
      throw e;
    }
    if (confirmed) {
      onSent(channel, msg);
      toast.success("Formulaire marqué « envoyé »");
    }
  };

  return (
    <div className="rg-section" id={anchorId} data-testid={`${testid}-block`}>
      <h3>{icon} {title}</h3>
      {subtitle && (
        <p className="rg-muted" style={{ marginTop: -4, marginBottom: 12, fontSize: 12 }}>{subtitle}</p>
      )}

      <div className="rg-card" style={{ padding: 12 }}>
        <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="rg-row" style={{ gap: 6, alignItems: "center" }}>
            <Link2 size={14} style={{ color: "var(--rg-text-3)" }} />
            <span style={{ fontWeight: 500, fontSize: 13 }}>{state === "received" ? "Formulaire reçu" : state === "sent" ? "Formulaire envoyé" : "Formulaire à envoyer"}</span>
          </div>
          <FormStatusBadge state={state} testid={`${testid}-status-${state}`} />
        </div>

        {state !== "received" && (
          <>
            <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              <button type="button" className="rg-btn small" disabled={busy || !token} onClick={() => sendVia("whatsapp")}
                data-testid={`${testid}-send-wa`} style={{ opacity: hasPhone ? 1 : 0.7 }}>
                <MessageCircle size={12} /> Envoyer sur WhatsApp
              </button>
              <button type="button" className="rg-btn ghost small" disabled={busy || !token} onClick={() => sendVia("sms")} data-testid={`${testid}-send-sms`}>
                <Send size={12} /> SMS
              </button>
              <button type="button" className="rg-btn ghost small" onClick={copyLink} data-testid={`${testid}-copy-link`}>
                <Copy size={12} /> {copied ? "Lien copié !" : "Copier le lien"}
              </button>
              <a className="rg-btn ghost small" href={previewLink || "#"} target="_blank" rel="noopener noreferrer"
                data-testid={`${testid}-preview`} style={{ pointerEvents: previewLink ? "auto" : "none", opacity: previewLink ? 1 : 0.5 }}>
                <ExternalLink size={12} /> Prévisualiser le formulaire
              </a>
            </div>
            {!hasPhone && (
              <p className="rg-tiny" style={{ color: "#8a6d1a", marginTop: 6 }}>
                Aucun numéro valide sur cette fiche — WhatsApp/SMS afficheront une erreur. Utilise « Copier le lien » en attendant.
              </p>
            )}
            {state === "sent" && (
              <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                <button type="button" className="rg-btn ghost small" disabled={busy} onClick={onCancel} data-testid={`${testid}-cancel-send`}>
                  <RotateCcw size={11} /> Annuler l&apos;envoi
                </button>
              </div>
            )}
          </>
        )}

        {state === "received" && (
          <div style={{ marginTop: 10 }}>
            {receivedNode}
            <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              <a className="rg-btn ghost small" href={previewLink || "#"} target="_blank" rel="noopener noreferrer"
                data-testid={`${testid}-preview-received`} style={{ pointerEvents: previewLink ? "auto" : "none", opacity: previewLink ? 1 : 0.5 }}>
                <ExternalLink size={12} /> Prévisualiser le formulaire
              </a>
              <button type="button" className="rg-btn ghost small" disabled={busy} onClick={onReopen} data-testid={`${testid}-reopen`}>
                <RotateCcw size={11} /> Rouvrir (renvoyer le formulaire)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
